import { Box, Chip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import moment from "moment";
import "moment-timezone";
import { useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { useNavigate } from "react-router-dom";
import {
  AppointmentChangeStatus,
  AppointmentStatusChangeModal,
} from "@/components/appointment-status-change";
import Flex from "../../../../../../components/Flex";
import SuccessModal, {
  useSuccess,
} from "../../../../../../components/SuccessModal";
import Text from "../../../../../../components/Text";
import ViewAppointment from "../../../../../../components/ViewAppointment";
import CustomTitle from "../../../../../../components/app/CustomTitle";
import StyledButtonV2 from "../../../../../../components/app/StyledButtonV2";
import { PROVIDER_PATHS } from "../../../../../../constants/paths";
import { AppointmentSchedulingType } from "../../../../../../dummy-data/appointment-list-data-scheduling";
import useCurrentUser from "../../../../../../hooks/local-storage/use-current-user";
import { useReduceDisclosure } from "../../../../../../hooks/use-reduce-disclosure";
import { useAppointmentsV2 } from "../../../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { useGetAppointment } from "../../../../../../queries/use-get-appointment";
import {
  getDateWithOffsetByMinusHr,
  getStartEndDateOfSameDay,
  NonNullableDate,
} from "../../../../../../utils/date-time-utility";
import { getXtenantId } from "../../../../../../utils/utils";
import RescheduleAppointment from "../../../scheduling/appointment/RescheduleAppointment";
import { EventType } from "../../../scheduling/availability/availability-contant";
import DashboardEventWrapper from "./DashboardEventWrapper";
import CheckIn from "../../../scheduling/appointment/CheckIn";
import { getStoredRealm } from "@/utils/auth-storage";

const DashboardCalender = () => {
  const { endTime, startTime } = getStartEndDateOfSameDay(dayjs(new Date()));
  const localizer = momentLocalizer(moment);
  const [me] = useCurrentUser();
  const navigate = useNavigate();
  const { data: appointmentsList } = useAppointmentsV2({
    pagination: { page: 0, pageSize: 100 },
    startDate: startTime ?? undefined,
    endDate: endTime ?? undefined,
    providerUuid: me?.uuid ? [me.uuid] : undefined,
    providerGroupId: getXtenantId(),
    appointmentStatus: [
      "PENDING",
      "CONFIRMED",
      "CHECKED_IN",
      "IN_EXAM",
      "INSTANT",
    ],
    appStatusForPatient: ["CONFIRMED"],
  });

  const EVENT: EventType[] | undefined = useMemo(
    () =>
      appointmentsList?.data?.content?.map((it) => {
        return {
          ...it,
          title: `${it?.patientName}`,
          start: new Date(it.startTime),
          end: new Date(it.endTime),
          status: it.status,
          mode: it?.mode,
          type: it?.type,
          patientName: it?.patientName,
        };
      }),
    [appointmentsList?.data?.content]
  );

  // const eventPropGetter = () => ({
  //   style: {
  //     backgroundColor: "transparent",
  //     color: "black",
  //   },
  // });

  const [
    appointmentStatusChangeDisclosure,
    rescheduleDisclosure,
    appointmentDisclosure,
    checkInDrawer,
  ] = useReduceDisclosure(4);
  const [selectedAppointment, setSelectedAppointment] =
    useState<AppointmentSchedulingType>();
  const [value, setValue] = useState("appointment");

  const client = useQueryClient();
  const [statusChangedTo, setStatusChangedTo] =
    useState<AppointmentChangeStatus>();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [
    openedSuccessModal,
    { open: openSuccessModal, close: closeSucessModal },
  ] = useSuccess();

  const { data: AppointmentData } = useGetAppointment({
    appointmentUuid: selectedAppointment?.uuid,
    providerGroupId: getStoredRealm() ?? "",
  });

  return (
    <Box
      sx={{
        /* ---- Remove grid look ---- */
        "& .rbc-time-view": {
          border: "none",
        },
        "& .rbc-time-content": {
          borderTop: "none",
        },
        "& .rbc-time-content > * + * > *": {
          borderLeft: "none",
        },
        "& .rbc-timeslot-group": {
          borderBottom: "1px solid #E5E7EB",
          minHeight: "120px",
        },
        "& .rbc-day-slot .rbc-time-slot": {
          borderTop: "none",
          background: "transparent !important",
          backgroundColor: "transparent !important",
          color: "#6B7280",
          fontSize: "12px",
        },
        "& .rbc-time-slot": {
          background: "transparent !important",
          backgroundColor: "transparent !important",
        },
        "& .rbc-time-header": {
          display: "none",
        },
        "& .rbc-allday-cell": {
          display: "none",
        },

        /* ---- Time gutter (left column) ---- */
        "& .rbc-time-gutter .rbc-timeslot-group": {
          borderBottom: "none",
          display: "flex",
          alignItems: "flex-start",
          paddingTop: "4px",
        },
        "& .rbc-label": {
          color: "#6B7280",
          fontSize: "12px",
          fontWeight: 400,
          padding: "0 8px 0 0",
        },

        /* ---- Event cards ---- */
        "& .rbc-event": {
          border: "none !important",
          outline: "none",
          boxShadow: "none",
          padding: "0 !important",
          backgroundColor: "transparent !important",
        },
        "& .rbc-event:focus": {
          outline: "none",
        },
        "& .rbc-event-label": {
          display: "none !important",
        },
        "& .rbc-event-content": {
          height: "100%",
          overflow: "hidden",
          "& > div": {
            height: "100%",
          },
        },
        "& .rbc-events-container": {
          marginRight: "8px",
        },

        /* ---- Current time indicator ---- */
        "& .rbc-current-time-indicator": {
          backgroundColor: "#104E84",
          height: "2px",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "-5px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: "#104E84",
          },
        },

        /* ---- Remove day bg borders ---- */
        "& .rbc-day-slot .rbc-events-container": {
          marginLeft: "0",
        },
        "& .rbc-day-bg": {
          border: "none",
        },
        "& .rbc-today": {
          backgroundColor: "transparent !important",
        },
      }}
    >
      {appointmentDisclosure.opened && (
        <ViewAppointment
          open={appointmentDisclosure.opened}
          onClose={appointmentDisclosure.close}
          activeTab={value}
          handleTabChange={(val) => setValue(val)}
          openChangeStatus={(statusChanged: AppointmentChangeStatus) => {
            appointmentStatusChangeDisclosure.open();
            setStatusChangedTo(statusChanged);
          }}
          openRescheduleAppointment={rescheduleDisclosure.open}
          openCheckIn={checkInDrawer.open}
          appointmentUuid={selectedAppointment?.uuid}
        />
      )}

      {checkInDrawer.opened && (
        <CheckIn
          appointMentData={selectedAppointment}
          disclosureHost={checkInDrawer}
          patientId={selectedAppointment?.patientId}
          appointmentUuid={selectedAppointment?.uuid}
        />
      )}

      {rescheduleDisclosure.opened && (
        <RescheduleAppointment
          appointmentData={AppointmentData?.data}
          successHandler={(msg: string) => {
            openSuccessModal();
            setSuccessMessage(msg);
            appointmentStatusChangeDisclosure.close();
            rescheduleDisclosure.close();
            appointmentDisclosure.close();
            client.invalidateQueries({ queryKey: ["appointment-list-v2"] });
          }}
          opened={rescheduleDisclosure.opened}
          onClose={rescheduleDisclosure.close}
        />
      )}

      <AppointmentStatusChangeModal
        disclosureHost={appointmentStatusChangeDisclosure}
        appointmentId={selectedAppointment?.uuid?.toString()}
        status={statusChangedTo}
        onSuccess={(message) => {
          appointmentDisclosure.close();
          openSuccessModal();
          setSuccessMessage(message);
        }}
      />

      {openedSuccessModal && (
        <SuccessModal
          label={successMessage}
          opened={openedSuccessModal}
          onClose={closeSucessModal}
        />
      )}

      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap={1} alignItems="center">
          <CustomTitle>Today's Appointments</CustomTitle>
          <Chip
            label={appointmentsList?.data?.page?.totalElements ?? 0}
            sx={{
              fontWeight: 600,
              fontSize: "11px",
              color: "black",
              backgroundColor: "#AEDAFF",
              height: "28px",
              minWidth: "28px",
              borderRadius: "50%",
            }}
            size="small"
          />
        </Flex>
        <StyledButtonV2
          onClick={() =>
            navigate(
              PROVIDER_PATHS.APPOINTMENTS +
                `?defaultFilter=true&filterDateType=day`
            )
          }
          variant="text"
          label="View All"
        />
      </Flex>
      <Flex sx={{ paddingLeft: "12px", color: "#595F63" }}>
        <Text sx={{ marginLeft: "4px" }}>
          {startTime ? NonNullableDate(startTime as any) : ""}
        </Text>
      </Flex>
      <Calendar
        localizer={localizer}
        events={EVENT}
        startAccessor="start"
        endAccessor="end"
        step={15}
        timeslots={4}
        dayLayoutAlgorithm="no-overlap"
        date={new Date()}
        view={"day"}
        scrollToTime={getDateWithOffsetByMinusHr(new Date(), 3)}
        // onShowMore={showMoreDisclosure.open}
        // onNavigate={(date) => calcFilterDate(view, date)}
        eventPropGetter={() => {
          return {
            style: {
              backgroundColor: "transparent",
              borderRadius: 8,
              color: "black",
              maxWidth: "96%",
              marginLeft: "2%",
              marginBottom: "4px",
              minHeight: "21px",
              border: "none",
              boxShadow: "none",
              padding: 0,
            },
          };
        }}
        style={{
          height: "calc(100vh - 300px)",
          fontFamily: "roboto",
        }}
        onSelectEvent={(newSelectedAppointment) => {
          appointmentDisclosure.open();
          setSelectedAppointment(newSelectedAppointment as any);
        }}
        components={{
          toolbar: () => <></>,
          event: (props) => <DashboardEventWrapper props={props} />,
        }}
      />
    </Box>
  );
};

export default DashboardCalender;
